import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
// markercluster expects window.L; ensure it's set before requiring
if (typeof window !== "undefined") window.L = L;
require("leaflet.markercluster");
import { api, apiError, ASSETS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Loader } from "@/components/Atoms";
import { Map as MapIcon } from "lucide-react";

// Mirror of the backend's `_slugify` (api/routes/live.py:75) so we can
// match link records / raid `server` names against `liveServers[].slug`.
// The backend is just `re.sub(r"[^a-z0-9]+", "-", name.lower()).strip("-")`
// — apostrophes count as non-alphanumeric and get COLLAPSED into a dash
// alongside the surrounding space ("Chillet's Revival" → "chillet-s-revival").
// An older version of this helper stripped quotes/apostrophes first,
// which produced "chillets-revival" and silently broke raid-marker
// filtering on the map.
const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
import { TILE_SIZE, SCALE, makeIcon, tileToGame, categorySpriteUrl, CATEGORY_SUBTYPES, subtypeOf, markerColor } from "@/components/map/markerIcons";
import { MapSidebar } from "@/components/map/MapSidebar";
import { MarkerPopup } from "@/components/map/MarkerPopup";
import { GuildBasePopup } from "@/components/map/GuildBasePopup";
import { PayoutFlash } from "@/components/PayoutFlash";

export default function MapPage() {
  const { user } = useAuth();
  const toast = useToast();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const layersRef = useRef({});
  const [markers, setMarkers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hidden, setHidden] = useState(new Set());
  // Per-category hidden sub-types, e.g. { resource: Set("coal"), … }.
  // Empty/absent Set means "show every sub-type".
  const [subHidden, setSubHidden] = useState({});
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop-only sidebar collapse toggle. Default expanded (`false`)
  // so power users see all filters at once; clicking the chevron in
  // the sidebar shrinks the 288 px pinned column to a 56 px icon
  // rail and widens the map for analysis. The toggle is hidden on
  // mobile (where the sidebar is already a drawer).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mapKind, setMapKind] = useState("world"); // "world" | "tree"
  const tileLayerRef = useRef(null);
  // Body-scroll lock while the mobile drawer is open — without this
  // the map underneath scrolls when the user flicks through the
  // sidebar list, which feels broken. Desktop's pinned sidebar
  // doesn't need this guard.
  useEffect(() => {
    if (!sidebarOpen) return;
    const isMobile = window.matchMedia("(max-width: 1023px)").matches;
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [sidebarOpen]);

  // Leaflet doesn't auto-detect when its container's width changes —
  // when the desktop sidebar collapses (`lg:pl-72` → `lg:pl-14`) the
  // tile pane stays at its old size and the visible map area shows a
  // blank background until something forces a re-layout. We also
  // can't *animate* the padding change because Leaflet observes the
  // container and ends up recomputing tile widths to 0 during the
  // half-way frames (verified in the browser 2026-02-22). So:
  //   1. The container className snaps padding instantly (no
  //      `transition-[padding]`).
  //   2. We schedule `invalidateSize()` on the next frame so React
  //      has committed the className first, then Leaflet remeasures
  //      cleanly. `animate: false, pan: false` keeps the visitor's
  //      current center/zoom locked in place.
  useEffect(() => {
    if (!mapRef.current) return;
    const raf = requestAnimationFrame(() => {
      try { mapRef.current?.invalidateSize({ animate: false, pan: false }); }
      catch { /* map unmounted between schedule and tick */ }
    });
    return () => cancelAnimationFrame(raf);
  }, [sidebarCollapsed]);

  // Same problem, different trigger: the container also changes size when
  // the phone rotates or the mobile URL bar collapses/expands (the
  // `.map-viewport` height is in `dvh`, so it moves with it). Without a
  // remeasure Leaflet keeps its old dimensions and paints grey bands
  // where tiles should be. Debounced so a pinch/rotate doesn't thrash it.
  useEffect(() => {
    let timer = 0;
    const remeasure = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        try { mapRef.current?.invalidateSize({ animate: false, pan: false }); }
        catch { /* map not mounted */ }
      }, 150);
    };
    window.addEventListener("resize", remeasure);
    window.addEventListener("orientationchange", remeasure);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("orientationchange", remeasure);
    };
  }, []);
  const [hideCollected, setHideCollected] = useState(false);
  // Pal-spawn opt-in state
  const [spawnPals, setSpawnPals] = useState([]);    // [{name, count}]
  const [spawnPal, setSpawnPal] = useState("");      // selected pal name
  const [spawnCycle, setSpawnCycle] = useState("");  // "" | "day" | "night"
  const [spawnMarkers, setSpawnMarkers] = useState([]);
  // Live players state
  const [liveServers, setLiveServers] = useState([]);
  const [liveServer, setLiveServer] = useState("");
  const [livePlayers, setLivePlayers] = useState([]);
  const [liveStatus, setLiveStatus] = useState(null);
  // Default ON so the map opens with live players visible first
  const [liveOn, setLiveOn] = useState(true);
  const livePlayerLayerRef = useRef(null);
  // Player base camps (from PalDefender exportguilds) — opt-in because
  // it adds 27+ extra markers per server.
  const [basesOn, setBasesOn] = useState(false);
  const [bases, setBases] = useState([]);
  const basesLayerRef = useRef(null);
  // PalDefender zone polygons (safe zones / no-build areas). Areas rather
  // than points, so they live on their own layer instead of the marker
  // cluster, and sit UNDER the pins so they never block a marker click.
  const [zonesOn, setZonesOn] = useState(false);
  const [zones, setZones] = useState([]);
  const [zonesAvailable, setZonesAvailable] = useState(false);
  const zonesLayerRef = useRef(null);
  // When the user clicks a base pin we open a richer guild-detail popup.
  const [openBaseGuild, setOpenBaseGuild] = useState(null);
  // Daily Treasure Hunt state
  const [hunt, setHunt] = useState(null);
  const huntLayerRef = useRef(null);
  // Daily Hunt bonus cinematic — fires once when the +bonus auto-claim
  // lands (server-side via /hunt/check). Re-using the same skull burst
  // we built for Gambling wins keeps the payout language consistent.
  const [bonusFlash, setBonusFlash] = useState(null);
  // Active raid bosses — pin a clickable pal-portrait at every raid
  // spawn point. Polls /api/raids/active every 60 s like the banner.
  const [raids, setRaids] = useState([]);
  const raidsLayerRef = useRef(null);
  // Track whether we've applied the "hide all static categories" default yet
  const defaultHiddenAppliedRef = useRef(false);
  // Live cursor coordinates overlay (tile-space, matches marker x/y)
  const [cursor, setCursor] = useState(null);

  // Which Palworld characters the current web user has linked, per
  // server. Used by the "Pan to me" button so we know which entry in
  // `livePlayers` corresponds to the visitor's own character.
  const [linkedByServer, setLinkedByServer] = useState({});  // {slug: player_name}
  useEffect(() => {
    if (!user) { setLinkedByServer({}); return; }
    let cancelled = false;
    api.get("/linking/status")
      .then(({ data }) => {
        if (cancelled) return;
        const out = {};
        for (const r of data.linked_servers || []) {
          // server slug must match the liveServers list (kebab-case).
          out[slugify(r.server_name)] = r.player_name;
        }
        setLinkedByServer(out);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Swap between the main world map and the World Tree region map.
  // The Tree map uses a DIFFERENT coord projection (PST
  // palworld_coord.__treemap_*: scale 724, ±2500 logical range, pixel
  // offsets 1760/2571 on the 8192 canvas — our treemap.png is
  // byte-identical to PST's T_TreeMap, so its calibration applies
  // verbatim). As of 2026-07-16 the backend converts every live player
  // and guild base into the RIGHT zone's tile space (`map: "world" |
  // "tree"` per row, game_to_zone_tile), and the pin/base effects below
  // filter by the active tab — fixing the 2026-07-11 report of players
  // rendering misplaced on the tree map. Static wiki-marker layers
  // (spawns, effigies, dungeons, raids) remain main-map-only: they're
  // authored against main-map coords and have no tree equivalents.
  useEffect(() => {
    if (!mapRef.current || !tileLayerRef.current) return;
    const dir = mapKind === "tree" ? "treemap_tiles" : "tiles";
    tileLayerRef.current.setUrl(`${ASSETS}/map/${dir}/{z}/{x}/{y}.png`);
    // Live players + guild bases layers stay ATTACHED on both maps; their
    // render effects filter rows to the active zone, so each tab shows
    // only the pins that belong to it. Static wiki-marker layers are
    // main-map-only (authored against main-map coords).
    const map = mapRef.current;
    const alwaysVisible = new Set([
      livePlayerLayerRef.current,
      basesLayerRef.current,
      zonesLayerRef.current,
      // Static markers are zone-filtered in their render effect (tree
      // fast travels carry metadata.map === "tree"), so the cluster
      // layer itself stays attached on both tabs.
      clusterRef.current,
    ]);
    const mainMapOnly = [
      huntLayerRef.current,
      raidsLayerRef.current,
    ];
    mainMapOnly.forEach((layer) => {
      if (!layer) return;
      if (mapKind === "tree") {
        if (map.hasLayer(layer)) map.removeLayer(layer);
      } else {
        if (!map.hasLayer(layer)) map.addLayer(layer);
      }
    });
    // Ensure the always-visible layers are actually on the map.
    alwaysVisible.forEach((layer) => {
      if (layer && !map.hasLayer(layer)) map.addLayer(layer);
    });
  }, [mapKind]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      crs: L.CRS.Simple,
      minZoom: 0,
      maxZoom: 5,
      zoomControl: true,
      attributionControl: true,
    });
    L.tileLayer(`${ASSETS}/map/tiles/{z}/{x}/{y}.png`, {
      minZoom: 0, maxZoom: 5, tileSize: TILE_SIZE, noWrap: true,
      bounds: [[-TILE_SIZE, 0], [0, TILE_SIZE]],
      attribution: 'Markers © <a href="https://palworld.fandom.com/wiki/Map:Palpagos_Islands" target="_blank" rel="noopener">Palworld Wiki</a> CC-BY-SA',
    }).addTo(map);
    // Store the ref so the "World Map ↔ Tree Map" toggle can swap its
    // URL without reinitialising the whole Leaflet instance.
    tileLayerRef.current = map._layers && Object.values(map._layers)[0];
    map.fitBounds([[-TILE_SIZE, 0], [0, TILE_SIZE]]);
    map.setMaxBounds([[-TILE_SIZE - 32, -32], [32, TILE_SIZE + 32]]);
    map.zoomControl.setPosition('bottomright');

    // Marker clustering — keep clusters as a navigational aid at lower
    // zooms, but at the deepest zoom level (5) we disable clustering
    // entirely so every marker renders at its *exact* world coordinates.
    // Spiderfy is also off: previously overlapping markers were fanned
    // out in a synthetic ring, which hid their true positions.
    clusterRef.current = L.markerClusterGroup({
      maxClusterRadius: 40,
      spiderfyOnMaxZoom: false,
      disableClusteringAtZoom: 5,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      iconCreateFunction: (cluster) => L.divIcon({
        html: `<div class="cluster-pin">${cluster.getChildCount()}</div>`,
        className: "marker-cluster-skull",
        iconSize: [38, 38],
      }),
    });
    // Zone polygons go on before the cluster so they paint underneath every
    // pin; a filled polygon above the markers would swallow their clicks.
    zonesLayerRef.current = L.layerGroup().addTo(map);
    map.addLayer(clusterRef.current);
    // Dedicated layer for live player pins (lives ABOVE clusters)
    livePlayerLayerRef.current = L.layerGroup().addTo(map);
    // Dedicated layer for guild base camps (PalDefender exportguilds overlay)
    basesLayerRef.current = L.layerGroup().addTo(map);
    // Dedicated layer for daily Treasure Hunt pins (always visible regardless of category filters)
    huntLayerRef.current = L.layerGroup().addTo(map);
    // Dedicated layer for active raid boss markers — sits above
    // everything so the pal-portrait is impossible to miss.
    raidsLayerRef.current = L.layerGroup().addTo(map);

    // Live cursor coordinate readout — convert mousemove latlng → tile-space
    // (the same x/y coordinate space used by every marker). Helpful for
    // identifying markers visually when reporting bad placements.
    const onMove = (e) => {
      const x = e.latlng.lng / SCALE;
      const y = -e.latlng.lat / SCALE;
      setCursor({ x, y });
    };
    const onLeave = () => setCursor(null);
    map.on("mousemove", onMove);
    map.on("mouseout", onLeave);

    mapRef.current = map;
    return () => {
      map.off("mousemove", onMove);
      map.off("mouseout", onLeave);
      map.remove();
      mapRef.current = null;
      clusterRef.current = null;
      livePlayerLayerRef.current = null;
      basesLayerRef.current = null;
      zonesLayerRef.current = null;
      huntLayerRef.current = null;
      raidsLayerRef.current = null;
    };
  }, []);

  // Fetch markers + categories (independent so a slow spawn list doesn't
  // block the rest)
  useEffect(() => {
    api.get("/map")
      .then(({ data }) => setMarkers(data.markers || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
    api.get("/map/categories")
      .then(({ data }) => {
        const cats = data.categories || [];
        setCategories(cats);
        // First time we receive categories, default to hiding static
        // marker categories so the map opens clean — EXCEPT fast travels
        // (both maps), which are the primary orientation markers and are
        // shown by default (owner request 2026-07-17). Everything else
        // can be re-enabled from the sidebar.
        if (!defaultHiddenAppliedRef.current && cats.length > 0) {
          const shownByDefault = new Set(["fast_travel", "fast_travel_tree"]);
          setHidden(new Set(cats.map((c) => c.category).filter((c) => !shownByDefault.has(c))));
          defaultHiddenAppliedRef.current = true;
        }
      })
      .catch(() => {});
    api.get("/map/pal-spawn-pals")
      .then(({ data }) => setSpawnPals(data.pals || []))
      .catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  // Fetch pal_spawn markers when the user picks a pal/cycle
  useEffect(() => {
    if (!spawnPal) { setSpawnMarkers([]); return; }
    const params = new URLSearchParams({ category: "pal_spawn", pal: spawnPal });
    if (spawnCycle) params.set("cycle", spawnCycle);
    api.get(`/map?${params}`)
      .then(({ data }) => setSpawnMarkers(data.markers || []))
      .catch((e) => toast.error(apiError(e, "Failed to load spawns")));
  }, [spawnPal, spawnCycle]);

  // Load the list of available Palworld servers once
  useEffect(() => {
    api.get("/live/servers")
      .then(({ data }) => {
        const list = data.servers || [];
        setLiveServers(list);
        if (list.length > 0) {
          // Honour `?server=<slug>` from the URL if the dashboard
          // server-card sent us here AND the slug matches a known
          // server. Otherwise fall back to the first server in the
          // list. Doing the deep-link check INSIDE this fetch (vs a
          // separate effect that races with this one) guarantees
          // we don't first set Revival and then bounce to Modded,
          // which flickered the dropdown and broke the auto-pan
          // sequencing.
          let requested = "";
          try {
            requested = new URLSearchParams(window.location.search).get("server") || "";
          } catch { /* ignore */ }
          const match = requested && list.find((s) => s.slug === requested);
          setLiveServer(match ? match.slug : list[0].slug);
        }
      })
      .catch(() => {});
  }, []);

  // Poll live status + players every 5 s when the live overlay is enabled
  useEffect(() => {
    if (!liveOn || !liveServer) {
      setLivePlayers([]);
      setLiveStatus(null);
      return;
    }
    let cancelled = false;
    const tick = () => {
      api.get(`/live/${liveServer}/status`).then(({ data }) => { if (!cancelled) setLiveStatus(data); }).catch(() => {});
      api.get(`/live/${liveServer}/players`)
        .then(({ data }) => { if (!cancelled) setLivePlayers(data.players || []); })
        .catch(() => {});
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
  }, [liveOn, liveServer]);

  // Render live player pins on top of everything else
  useEffect(() => {
    const map = mapRef.current;
    const layer = livePlayerLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    if (!liveOn) return;
    for (const p of livePlayers) {
      // Each player carries `map: "world" | "tree"` with tile coords in that
      // zone's tile space (backend game_to_zone_tile, PST-parity projection).
      // Only draw pins belonging to the map that's currently displayed.
      const zone = p.map || "world";
      if ((mapKind === "tree") !== (zone === "tree")) continue;
      const lat = -p.tile_y * SCALE;
      const lng = p.tile_x * SCALE;
      const icon = L.divIcon({
        className: "leaflet-live-player",
        html: `<div class="live-player-pin"><span class="live-player-dot"></span><span class="live-player-label">${p.name} · Lv ${p.level}</span></div>`,
        iconSize: [12, 12],
        iconAnchor: [6, 6],
      });
      L.marker([lat, lng], { icon, zIndexOffset: 1000 })
        .bindTooltip(`<b>${p.name}</b>${(p.map === "tree") ? " · 🌳 World Tree" : ""}<br/>Lv ${p.level} · ${p.ping}ms<br/>Buildings: ${p.building_count}`, { direction: "top" })
        .addTo(layer);
    }
  }, [livePlayers, liveOn, mapKind]);

  // ---------------------------------------------------------------------
  // Player guild base camps (PalDefender exportguilds → SFTP → tile coords).
  // Cached server-side for 5 minutes so toggling the layer off/on doesn't
  // hammer the upstream RCON.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!basesOn || !liveServer) { setBases([]); return; }
    let cancelled = false;
    api.get(`/live/${liveServer}/bases`)
      .then(({ data }) => { if (!cancelled) setBases(data.bases || []); })
      .catch(() => { if (!cancelled) setBases([]); });
    return () => { cancelled = true; };
  }, [basesOn, liveServer]);

  // -------------------------------------------------------------------
  // PalDefender zone polygons. Fetched once per server (the config only
  // changes when an admin edits it), independent of the toggle so we know
  // whether to offer the toggle at all — a server with no zones file
  // shouldn't show a control that does nothing.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!liveServer) { setZones([]); setZonesAvailable(false); return; }
    let cancelled = false;
    api.get(`/map/zones?server=${encodeURIComponent(liveServer)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setZones(data.zones || []);
        setZonesAvailable(!!data.available && (data.zones || []).length > 0);
      })
      .catch(() => { if (!cancelled) { setZones([]); setZonesAvailable(false); } });
    return () => { cancelled = true; };
  }, [liveServer]);

  useEffect(() => {
    const layer = zonesLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!zonesOn) return;
    for (const z of zones) {
      // Zone-aware like the other overlays — only draw shapes belonging to
      // the map currently on screen.
      if ((mapKind === "tree") !== ((z.map || "world") === "tree")) continue;
      const latlngs = (z.points || []).map(([tx, ty]) => [-ty * SCALE, tx * SCALE]);
      if (latlngs.length < 3) continue;
      const poly = L.polygon(latlngs, {
        color: z.color,
        weight: 2,
        opacity: 0.9,
        fillColor: z.color,
        fillOpacity: 0.15,
      });
      poly.bindTooltip(
        `<strong>${z.name}</strong><br/>${z.kind_label}` +
        (z.level_requirement > 1 ? `<br/>Level ${z.level_requirement}+` : ""),
        { sticky: true, className: "zone-tooltip" },
      );
      layer.addLayer(poly);
    }
  }, [zonesOn, zones, mapKind]);


  // Pre-warm the bases cache in the background as soon as the active
  // server is known. When the user later flips the Guild Bases toggle
  // ON, the server response is already cached → bases render instantly
  // instead of waiting 3-6 s for the RCON+SFTP roundtrip.
  useEffect(() => {
    if (!liveServer) return;
    const ctrl = new AbortController();
    api.get(`/live/${liveServer}/bases`, { signal: ctrl.signal })
      .catch(() => {});
    return () => ctrl.abort();
  }, [liveServer]);

  useEffect(() => {
    const layer = basesLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!basesOn) return;
    for (const b of bases) {
      // Zone-aware like player pins: only bases belonging to the active map.
      const zone = b.map || "world";
      if ((mapKind === "tree") !== (zone === "tree")) continue;
      const lat = -b.tile_y * SCALE;
      const lng = b.tile_x * SCALE;
      const icon = L.divIcon({
        className: "leaflet-guild-base",
        // Custom base icon ships in /public/icons/base.png and is rotated 0°
        // (it's a top-down illustration of a Palworld guild compound).
        html: `<img src="/icons/base.png" alt="" class="guild-base-icon" />`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
      L.marker([lat, lng], { icon, zIndexOffset: 800 })
        .bindTooltip(
          `<b>${b.guild_name}</b><br/>Leader: ${b.admin_name || "—"} · Lv ${b.guild_level}`,
          { direction: "top", offset: [0, -8] },
        )
        // Click → open the rich popup (members / chest / expeditions).
        .on("click", () => setOpenBaseGuild(b.guild || null))
        .addTo(layer);
    }
  }, [bases, basesOn, mapKind]);

  // ---------------------------------------------------------------------
  // Active raid bosses — poll /api/raids/active every 60 s and render a
  // pal-portrait marker at each raid's tile coordinates. Click → popup
  // with the rallying-cry message. Public endpoint, so logged-out
  // visitors also see the markers.
  // ---------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const fetchRaids = async () => {
      try {
        const { data } = await api.get("/raids/active");
        if (!cancelled) setRaids(Array.isArray(data?.raids) ? data.raids : []);
      } catch { /* silent */ }
    };
    fetchRaids();
    const id = setInterval(fetchRaids, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Deep-link landing: `…/map?raid=<id>` (the link shape used by the
  // raid-spawn Discord embed and the in-app RaidBanner). When the page
  // boots with a `?raid=…` query, we:
  //   1. Wait for both `liveServers` and `raids` to load.
  //   2. Look up the target raid by id and pick its server slug.
  //   3. Switch the live-server dropdown to that server so the user
  //      doesn't land on an empty map.
  //   4. Stash a pending-pan flag so the marker-render effect (which
  //      already fires when `[raids, liveServer]` change) can pan +
  //      open the matching marker's popup AFTER it places markers.
  // The ref-guard (`raidDeepLinkConsumedRef`) ensures we only apply
  // the deep-link once per mount — if the user manually switches
  // servers afterwards, we don't bounce them back.
  const deepLinkRaidId = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("raid") || "";
    } catch { return ""; }
  }, []);
  const raidDeepLinkConsumedRef = useRef(false);
  const pendingRaidPanRef = useRef(null);   // raid id to pan to after markers render
  useEffect(() => {
    if (!deepLinkRaidId || raidDeepLinkConsumedRef.current) return;
    if (!liveServers.length || !raids.length) return;
    const target = raids.find((r) => String(r.id) === String(deepLinkRaidId));
    if (!target) return;  // raid expired / unknown — leave defaults alone
    const targetSlug = slugify(target.server || "");
    if (!targetSlug) return;
    // Only switch if the matching server is actually in the live-servers
    // list — guards against a renamed/decommissioned server slug.
    if (!liveServers.some((s) => s.slug === targetSlug)) return;
    raidDeepLinkConsumedRef.current = true;
    pendingRaidPanRef.current = String(deepLinkRaidId);
    if (liveServer !== targetSlug) setLiveServer(targetSlug);
  }, [deepLinkRaidId, liveServers, raids, liveServer]);

  // Sister deep-link: `…/map?server=<slug>` (link shape used by the
  // Dashboard's "Now Online" server cards). The actual switch is
  // applied inside the `/live/servers` fetch above (so it can't race
  // with the default `setLiveServer(list[0].slug)` line). This memo
  // stays here purely to expose the requested slug to anything else
  // that might care (e.g. analytics) — kept for documentation, no
  // runtime side-effect.
  const deepLinkServerSlug = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("server") || "";
    } catch { return ""; }
  }, []);
  // Suppress an unused-var warning while preserving the memo as a
  // documented hook of the deep-link surface.
  void deepLinkServerSlug;

  useEffect(() => {
    const layer = raidsLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    // Discord application-emoji IDs — keep in sync with PalAssets.jsx
    // (the single source-of-truth lives in
    // /app/backend/data/element_emoji_ids.json on the server side).
    const ELEMENT_EMOJI_IDS = {
      fire:     "1485468512897073202",
      water:    "1485468513907773450",
      electric: "1485468514541240540",
      grass:    "1485468515128574074",
      ground:   "1485468515724034215",
      ice:      "1485468516986650675",
      dark:     "1485468517653413908",
      dragon:   "1485468518597263450",
      neutral:  "1485468519771541695",
    };
    const elementSrc = (el) => {
      const id = ELEMENT_EMOJI_IDS[(el || "").toLowerCase()];
      return id ? `https://cdn.discordapp.com/emojis/${id}.png?size=64` : null;
    };

    // Only show raids for the currently selected server — Modded
    // bosses on Demise should not pollute Revival's map view (and
    // vice versa). The backend returns `raid.server` as the display
    // name from sftp.yml ("Chillet's Modded Demise"); `liveServer`
    // is the slug. Slugify both sides so case / apostrophe drift
    // can't cause false negatives.
    const currentSlug = liveServer || "";
    const visibleRaids = currentSlug
      ? raids.filter((r) => slugify(r.server || "") === currentSlug)
      : raids;

    for (const r of visibleRaids) {
      if (r.tile_x == null || r.tile_y == null) continue;
      const lat = -Number(r.tile_y) * SCALE;
      const lng = Number(r.tile_x) * SCALE;
      // Pal portrait inside a pulsing element-tinted ring. Ring colour
      // comes from `.raid-marker--<element>` so neighbouring raids
      // read as distinct elements at a glance (red Bellanoir vs blue
      // Frostallion). Falls back to the default red glow when the
      // pal has no element on file.
      const elKey = (r.element || "").toLowerCase();
      const elModClass = elKey ? ` raid-marker--${elKey}` : "";
      const elSrc = elementSrc(elKey);
      // No portrait on file → show the Summoning Altar. Replaces the old
      // two-tier fallback (element badge, then a `⚔︎` glyph), which made a
      // missing portrait look like a different kind of marker. The element
      // modifier class stays on the div so the coloured ring still reads.
      const html = `<div class="raid-marker${elModClass}"><img src="${
        r.pal_image || `${ASSETS}/tech/summoning-altar.webp`
      }" alt=""/></div>`;
      const icon = L.divIcon({
        className: "leaflet-raid-marker",
        html,
        iconSize: [56, 56],
        iconAnchor: [28, 28],
      });
      const passives = (r.passives || []).map((p) => `<li>${p}</li>`).join("");
      const elementBadge = elSrc
        ? `<img src="${elSrc}" alt="${elKey}" class="raid-popup__element-icon"/>`
        : "";
      const popup = `
        <div class="raid-popup">
          <div class="raid-popup__title">${elementBadge} Active Boss Raid happening now at this location.</div>
          <div class="raid-popup__sub">Try and catch it first.</div>
          <div class="raid-popup__line"><b>${r.pal_name}</b> · Lv ${r.level}</div>
          <div class="raid-popup__line raid-popup__server">${r.server} — ${r.location_name}</div>
          <div class="raid-popup__cat raid-popup__cat--${r.category}">${r.category} build</div>
          <ul class="raid-popup__passives">${passives}</ul>
        </div>
      `;
      const marker = L.marker([lat, lng], { icon, zIndexOffset: 2000 })
        .bindPopup(popup, { maxWidth: 280 })
        .addTo(layer);
      // Deep-link pan: when the page boots with `?raid=<id>`, the
      // auto-switch effect above stashes the id in
      // `pendingRaidPanRef`. Once *this* raid's marker is on the
      // current layer, fly to it and pop its tooltip so the user
      // lands directly on the hunt instead of having to find it.
      if (pendingRaidPanRef.current && String(r.id) === pendingRaidPanRef.current) {
        pendingRaidPanRef.current = null;
        // Small delay so the server-switch + tile relayout settles
        // before Leaflet calculates the flyTo path. Otherwise the
        // map can pan to stale bounds.
        setTimeout(() => {
          try {
            if (!mapRef.current) return;
            mapRef.current.flyTo([lat, lng], 4, { duration: 0.6 });
            marker.openPopup();
          } catch { /* ignore — map may have been unmounted */ }
        }, 240);
      }
    }
  }, [raids, liveServer]);

  // ---------------------------------------------------------------------
  // Daily Treasure Hunt — fetch today's hunts + auto-claim heartbeat.
  // The browser only *triggers* a check; the backend reads live coords
  // from the Palworld REST API (un-spoofable) and credits skulls.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!user) { setHunt(null); return; }
    let cancelled = false;
    api.get("/hunt/daily")
      .then(({ data }) => { if (!cancelled) setHunt(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!user || !hunt || !hunt.linked) return;
    // Once all 3 (and bonus) are claimed there's nothing left to award today.
    if (hunt.all_done && hunt.bonus_claimed) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.post("/hunt/check");
        if (cancelled) return;
        setHunt(data);
        if (data.newly_claimed?.length) {
          for (const c of data.newly_claimed) {
            toast.success(`Treasure found: ${c.name} (+${c.reward} 🪙)`);
          }
        }
        if (data.bonus_just_claimed) {
          toast.success(`All 3 hunts done! Bonus +${data.bonus_reward} 🪙`);
          setBonusFlash({ amount: data.bonus_reward || 0, label: "DAILY BONUS" });
        }
        if (data.raid_triggered) {
          const r = data.raid_triggered;
          toast.success(
            `⚡ Bonus raid summoned! Lv ${r.level} ${r.pal_name} at ${r.location_name}`,
            { duration: 12000 }
          );
        }
      } catch { /* silent — keep heartbeat alive */ }
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user, hunt?.linked, hunt?.all_done, hunt?.bonus_claimed]); // eslint-disable-line

  // Render hunt markers on their own layer (always visible, ignores category filters)
  useEffect(() => {
    const layer = huntLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!hunt?.hunts?.length) return;
    for (const h of hunt.hunts) {
      const lat = -h.y * SCALE;
      const lng = h.x * SCALE;
      const icon = L.divIcon({
        className: "leaflet-hunt-marker",
        html: `<div class="hunt-pin ${h.claimed ? "claimed" : ""}"><span class="hunt-pin-glyph">${h.claimed ? "✓" : "?"}</span></div>`,
        iconSize: [34, 34],
        iconAnchor: [17, 34],
      });
      L.marker([lat, lng], { icon, zIndexOffset: 900 })
        .bindTooltip(
          `<b>Daily Hunt</b><br/>${h.claimed ? "Found ✓" : "Unclaimed"}<br/>Reward: ${h.reward_skulls} 🪙`,
          { direction: "top" }
        )
        .addTo(layer);
    }
  }, [hunt]);

  const flyToHunt = (h) => {
    const map = mapRef.current;
    if (!map) return;
    map.flyTo([-h.y * SCALE, h.x * SCALE], 4, { duration: 1.0 });
  };

  // Render markers when data or hidden changes
  useEffect(() => {
    const map = mapRef.current;
    const cluster = clusterRef.current;
    if (!map || !cluster) return;

    cluster.clearLayers();
    layersRef.current = {};

    const all = [...markers, ...spawnMarkers];
    const filtered = all.filter((m) => {
      // Zone-aware: markers tagged metadata.map === "tree" (World Tree
      // fast travels) belong to the tree canvas; everything else to the
      // main map. Only render pins for the tab that's active.
      const zone = m.metadata?.map === "tree" ? "tree" : "world";
      if ((mapKind === "tree") !== (zone === "tree")) return false;
      // Pal-spawn markers are explicitly opt-in via the picker, so they
      // should always render once fetched — independent of the "Hide all" /
      // category-solo state.
      if (m.category !== "pal_spawn" && hidden.has(m.category)) return false;
      // Sub-type filter (resource node types, fishing tiers)
      if (CATEGORY_SUBTYPES[m.category]) {
        const st = subtypeOf(m);
        if (st && subHidden[m.category]?.has(st)) return false;
      }
      if (search && !(m.name?.toLowerCase().includes(search.toLowerCase()) || m.category?.toLowerCase().includes(search.toLowerCase()))) return false;
      return true;
    });

    const layers = [];
    filtered.forEach((m) => {
      // Image coords (y=0 at top, 0-8192) → Leaflet zoom-0 (y goes up, image at -y, 0-256)
      const lat = -m.y * SCALE;
      const lng = m.x * SCALE;
      // Per-row icon_url (admin-set) wins; otherwise fall back to the
      // category's real game sprite (fast travel statue, alpha marker,
      // relic, chest, oil derrick…); glyph pin if neither exists.
      const level = m.category === "alpha_pal" ? m.metadata?.level : null;
      const marker = L.marker([lat, lng], {
        icon: makeIcon(m.category, m.collected, m.icon_url || categorySpriteUrl(ASSETS, m.category), {
          color: markerColor(m.category, subtypeOf(m)),
          badge: level ? `${level}` : "",
        }),
      });
      marker.on("click", () => {
        setActive(m);
        window.location.hash = `marker=${m.id}`;
      });
      layers.push(marker);
      layersRef.current[m.id] = marker;
    });
    cluster.addLayers(layers);

    // Open marker from hash on first load. If the marker's category is
    // currently hidden (e.g. arriving here from the Dashboard "Region
    // Highlights" card), un-hide it so the pin is actually rendered.
    const hash = window.location.hash;
    const match = hash.match(/marker=([^&]+)/);
    if (match) {
      const targetId = decodeURIComponent(match[1]);
      const target = all.find((m) => m.id === targetId);
      if (target && hidden.has(target.category)) {
        // Defer to next tick to avoid setHidden-in-effect anti-pattern warning
        setTimeout(() => {
          setHidden((p) => {
            const n = new Set(p);
            n.delete(target.category);
            return n;
          });
        }, 0);
        return;
      }
      const found = filtered.find((m) => m.id === targetId);
      if (found) {
        map.setView([-found.y * SCALE, found.x * SCALE], 3);
        setActive(found);
      }
    }
  }, [markers, spawnMarkers, hidden, subHidden, search, hideCollected, mapKind]);

  // Sub-type counts per category, built from the loaded markers so the
  // sidebar chips always match what's actually on the map.
  const subtypeGroups = useMemo(() => {
    const out = {};
    Object.keys(CATEGORY_SUBTYPES).forEach((cat) => {
      const counts = new Map();
      markers.forEach((m) => {
        if (m.category !== cat) return;
        const st = subtypeOf(m);
        if (!st) return;
        counts.set(st, (counts.get(st) || 0) + 1);
      });
      if (counts.size > 1) {
        out[cat] = [...counts.entries()]
          .map(([value, count]) => ({ value, count }))
          .sort((a, b) => b.count - a.count);
      }
    });
    return out;
  }, [markers]);

  const toggleSubtype = useCallback((cat, value) => {
    setSubHidden((p) => {
      const next = new Set(p[cat] || []);
      if (next.has(value)) next.delete(value); else next.add(value);
      return { ...p, [cat]: next };
    });
  }, []);

  const soloSubtype = useCallback((cat, value) => {
    setSubHidden((p) => {
      const all = (subtypeGroups[cat] || []).map((s) => s.value);
      const onlyThis = p[cat]?.size === all.length - 1 && !p[cat]?.has(value);
      return { ...p, [cat]: onlyThis ? new Set() : new Set(all.filter((v) => v !== value)) };
    });
  }, [subtypeGroups]);

  const resetSubtypes = useCallback((cat) => {
    setSubHidden((p) => ({ ...p, [cat]: new Set() }));
  }, []);

  const toggleCat = (cat) => {
    setHidden((p) => {
      const n = new Set(p);
      if (n.has(cat)) n.delete(cat); else n.add(cat);
      return n;
    });
  };

  const showAll = () => setHidden(new Set());
  const hideAll = () => setHidden(new Set(categories.map((c) => c.category)));
  const soloCategory = (cat) => {
    const onlyThis = categories.length - 1 === hidden.size && !hidden.has(cat);
    if (onlyThis) showAll();
    else setHidden(new Set(categories.filter((c) => c.category !== cat).map((c) => c.category)));
  };

  const markCollected = async (id, currentlyCollected) => {
    try {
      if (currentlyCollected) {
        await api.delete(`/map/progress/${id}`);
      } else {
        await api.post(`/map/progress/${id}`);
      }
      setMarkers((ms) => ms.map((m) => m.id === id ? { ...m, collected: !currentlyCollected } : m));
      setActive((a) => a?.id === id ? { ...a, collected: !currentlyCollected } : a);
      // Refresh category counts
      const { data } = await api.get("/map/categories");
      setCategories(data.categories || []);
    } catch (e) { toast.error(apiError(e)); }
  };

  const resetProgress = async () => {
    if (!window.confirm("Reset ALL your map progress?")) return;
    try {
      await api.delete("/map/progress");
      setMarkers((ms) => ms.map((m) => ({ ...m, collected: false })));
      const { data } = await api.get("/map/categories");
      setCategories(data.categories || []);
      toast.success("Progress reset");
    } catch (e) { toast.error(apiError(e)); }
  };

  const callTowerRun = async (markerId) => {
    try {
      const { data } = await api.post(`/map/tower-run/${markerId}`);
      toast.success(`Callout posted in Discord for ${data.bosses}!`);
    } catch (e) { toast.error(apiError(e, "Could not post callout")); }
  };

  // "Pan to me" — find the visitor's own Palworld character in the
  // live-player list and fly the map to their position. Returns the
  // matched player (or null) so the sidebar can render a disabled
  // state when the user is offline.
  const myLiveCharacter = useMemo(() => {
    const myName = linkedByServer[liveServer];
    if (!myName) return null;
    const needle = myName.toLowerCase();
    return livePlayers.find((p) => (p.name || "").toLowerCase() === needle) || null;
  }, [linkedByServer, liveServer, livePlayers]);

  const panToMe = useCallback(() => {
    if (!myLiveCharacter || !mapRef.current) return;
    mapRef.current.flyTo(
      [-myLiveCharacter.tile_y * SCALE, myLiveCharacter.tile_x * SCALE],
      4,
      { duration: 1.0 },
    );
  }, [myLiveCharacter]);

  return (
    // Height was `calc(100vh - 56px - 49px)`. Two bugs there:
    //   1. `100vh` on mobile is the URL-bar-COLLAPSED height, so the box
    //      ran 60-100px past what you can actually see.
    //   2. `- 49px` was a guess at the footer, which wraps to 3-4 lines
    //      on a narrow screen and is far taller than that.
    // Because MapSidebar is `absolute inset-y-0` INSIDE this box, the
    // drawer's scroll viewport ended below the fold and its last controls
    // (Reset Progress, Pal Spawns, tail of Categories) were unreachable.
    // `dvh` tracks the visible viewport; the footer simply sits below the
    // fold on this page, which is correct for a full-bleed map.
    // `.map-viewport` (index.css) carries the height with a vh fallback
    // ahead of the dvh value — two Tailwind arbitrary classes would race
    // on stylesheet order instead.
    <div className="-mx-4 sm:-mx-6 -mt-6 sm:-mt-10 relative map-viewport">
      {/* Map kind toggle — floats over the top-right of the Leaflet
          canvas. Two options: the main Palpagos Islands map (default)
          or the new World Tree region added in the 2026-07 update.
          Both share the same 8192² tile coord space so player pins
          overlay correctly on either. */}
      <div
        // top-16 on phones clears the mobile toolbar, which shares `top-3`.
        // z-[410] keeps map chrome BELOW the drawer (440) and its backdrop
        // (430) — at z-[1000] this used to float on top of the open drawer
        // and swallow taps meant for the raid/server pills underneath it.
        className="absolute top-16 right-3 lg:top-3 z-[410] flex rounded-lg overflow-hidden border border-white/10 shadow-lg backdrop-blur"
        style={{ background: "rgba(10,15,25,0.85)" }}
        data-testid="map-kind-toggle"
      >
        {[
          { id: "world", label: "🗺️ World" },
          { id: "tree",  label: "🌳 World Tree" },
        ].map((k) => (
          <button
            key={k.id}
            onClick={() => setMapKind(k.id)}
            className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
              mapKind === k.id
                ? "bg-[var(--teal)] text-black"
                : "text-[var(--muted)] hover:text-[var(--bone)] hover:bg-white/5"
            }`}
            data-testid={`map-kind-${k.id}`}
          >
            {k.label}
          </button>
        ))}
      </div>

      {/* World Tree progress — collected counts for the tree-zone
          markers (fast travels / alphas / bosses). Click a pin and hit
          "Mark found" to tick it off; requires login to persist. */}
      {mapKind === "tree" && (() => {
        const treeRows = markers.filter((m) => m.metadata?.map === "tree");
        const groups = [
          { cat: "fast_travel_tree", label: "Fast Travels", icon: "🗿" },
          { cat: "alpha_pal", label: "Alpha Pals", icon: "⭐" },
          { cat: "boss_tower", label: "Bosses", icon: "👑" },
        ].map((g) => {
          const rows = treeRows.filter((m) => m.category === g.cat);
          return { ...g, total: rows.length, done: rows.filter((m) => m.collected).length };
        }).filter((g) => g.total > 0);
        if (!groups.length) return null;
        return (
          <div
            className="absolute top-28 right-3 lg:top-14 z-[410] rounded-lg border border-white/10 shadow-lg backdrop-blur px-3 py-2 w-44"
            style={{ background: "rgba(10,15,25,0.85)" }}
            data-testid="tree-progress-card"
          >
            <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--muted)] mb-1.5">
              🌳 World Tree Progress
            </div>
            {groups.map((g) => (
              <div key={g.cat} className="mb-1.5 last:mb-0" data-testid={`tree-progress-${g.cat}`}>
                <div className="flex items-center justify-between text-[11px] text-[var(--bone)]">
                  <span>{g.icon} {g.label}</span>
                  <span className={g.done >= g.total ? "text-emerald-400 font-bold" : "text-[var(--muted)]"}>
                    {g.done}/{g.total}
                  </span>
                </div>
                <div className="h-1 mt-0.5 rounded bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded bg-[var(--teal)] transition-[width] duration-300"
                    style={{ width: `${g.total ? (g.done / g.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
            {!user && (
              <div className="text-[10px] text-[var(--muted)] mt-1.5">
                Log in &amp; click a pin → "Mark found" to track.
              </div>
            )}
          </div>
        );
      })()}
      <MapSidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        search={search}
        setSearch={setSearch}
        showAll={showAll}
        hideAll={hideAll}
        hideCollected={hideCollected}
        setHideCollected={setHideCollected}
        user={user}
        hunt={hunt}
        flyToHunt={flyToHunt}
        categories={categories}
        hidden={hidden}
        toggleCat={toggleCat}
        soloCategory={soloCategory}
        subtypeGroups={subtypeGroups}
        subHidden={subHidden}
        toggleSubtype={toggleSubtype}
        soloSubtype={soloSubtype}
        resetSubtypes={resetSubtypes}
        liveServers={liveServers}
        liveOn={liveOn}
        setLiveOn={setLiveOn}
        liveServer={liveServer}
        setLiveServer={setLiveServer}
        liveStatus={liveStatus}
        livePlayers={livePlayers}
        myLiveCharacter={myLiveCharacter}
        panToMe={panToMe}
        // Count of raids currently active on the selected server.
        // Filter mirrors the marker-render filter so the pill and the
        // map markers stay in lockstep.
        activeRaidCount={raids.filter((r) => slugify(r.server || "") === (liveServer || "")).length}
        basesOn={basesOn}
        setBasesOn={setBasesOn}
        bases={bases}
        zonesOn={zonesOn}
        setZonesOn={setZonesOn}
        zones={zones}
        zonesAvailable={zonesAvailable}
        mapRef={mapRef}
        spawnPals={spawnPals}
        spawnPal={spawnPal}
        setSpawnPal={setSpawnPal}
        spawnCycle={spawnCycle}
        setSpawnCycle={setSpawnCycle}
        spawnMarkers={spawnMarkers}
        resetProgress={resetProgress}
      />

      {/* Mobile-only top toolbar — pinned over the map. Gives users
          three things at-a-glance without opening the drawer:
            • Filters button (opens the drawer)
            • Current server name (the live-server context)
            • Active-raid pill when a hunt is on (tap → open drawer,
              which has the dedicated "jump to raid" pill)
          Hidden on desktop where the persistent left sidebar already
          shows all of this. */}
      {!sidebarOpen && (
      <div className="lg:hidden absolute top-3 left-3 right-3 z-[420] flex items-center gap-2 pointer-events-none">
        <button
          onClick={() => setSidebarOpen(true)}
          className="btn btn-sm pointer-events-auto"
          data-testid="map-sidebar-toggle"
        >
          <MapIcon size={14} /> Filters
        </button>
        {liveServer && (
          <div
            className="pointer-events-auto px-2.5 py-1.5 rounded-md glass-card text-xs text-[var(--bone)] truncate max-w-[55%]"
            data-testid="map-current-server-pill"
            title="Currently viewing"
          >
            {(liveServers.find((s) => s.slug === liveServer)?.name) || liveServer}
          </div>
        )}
        {raids.filter((r) => slugify(r.server || "") === (liveServer || "")).length > 0 && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="pointer-events-auto ml-auto px-2.5 py-1.5 rounded-md text-xs uppercase tracking-wider font-bold text-rose-100 bg-gradient-to-r from-rose-700/60 to-amber-700/40 border border-rose-500/40 animate-pulse"
            data-testid="map-mobile-raid-pill"
            title="A raid is live — tap to open"
          >
            ⚔︎ Raid
          </button>
        )}
      </div>
      )}

      {/* Dim backdrop — only renders while the mobile drawer is open.
          Tap to close (gives users an escape hatch without hunting
          for the X). Hidden on desktop where the sidebar is pinned. */}
      {sidebarOpen && (
        <div
          className="lg:hidden absolute inset-0 z-[430] bg-black/55 backdrop-blur-sm animate-slide-down-soft"
          onClick={() => setSidebarOpen(false)}
          data-testid="map-sidebar-backdrop"
          aria-hidden="true"
        />
      )}

      {/* Map — `isolation: isolate` creates a new CSS stacking context so
          Leaflet's internal panes (marker pane z-600, tooltip z-650,
          popup z-700) can't bleed above the sidebar (z-400) when zoomed in.

          IMPORTANT: the inner div that Leaflet attaches to MUST keep
          a stable className. Leaflet adds runtime classes
          (`leaflet-container`, `leaflet-grab`, `leaflet-touch-…`)
          directly via `el.classList.add()`. If React rewrites the
          className prop on that same node (e.g. toggling
          `lg:pl-72 ↔ lg:pl-14`), it overwrites the whole
          `className` string and Leaflet's runtime classes get
          wiped — the entire map disappears until refresh.
          We dodge this by wrapping it in a sibling div that owns
          the sidebar-aware padding instead. */}
      <div className={`w-full h-full ${sidebarCollapsed ? "lg:pl-14" : "lg:pl-72"}`}>
        <div
          ref={containerRef}
          className="w-full h-full"
          style={{ isolation: "isolate" }}
          data-testid="leaflet-map"
        />
      </div>

      {/* Live cursor coordinates — bottom-left of the map. Desktop
          only: touch devices have no hover cursor so this readout is
          dead weight on phones (and just covers the bottom of the
          map). Tracks the sidebar collapse width like the main map. */}
      {cursor && (
        <div
          className={`hidden lg:block absolute bottom-3 z-[410] px-3 py-1.5 rounded glass-card text-[11px] font-mono pointer-events-none select-none leading-tight ${sidebarCollapsed ? "left-[4.5rem]" : "left-[19rem]"}`}
          data-testid="cursor-coords"
        >
          {(() => {
            const g = tileToGame(cursor.x, cursor.y);
            return (
              <div>
                in-game: <span className="text-[var(--teal)]">{g.x}</span>,{" "}
                <span className="text-[var(--teal)]">{g.y}</span>
              </div>
            );
          })()}
        </div>
      )}
      {loading && <div className={`absolute inset-0 flex items-center justify-center bg-black/50 z-[405] ${sidebarCollapsed ? "lg:left-14" : "lg:left-72"}`}><Loader label="Loading map…" /></div>}

      <MarkerPopup
        marker={active}
        user={user}
        onClose={() => { setActive(null); window.location.hash = ""; }}
        onToggleCollected={markCollected}
        onCallTowerRun={callTowerRun}
      />

      <PayoutFlash
        show={bonusFlash !== null}
        variant="skull"
        amount={bonusFlash?.amount || 0}
        label={bonusFlash?.label || "DAILY BONUS"}
        onDone={() => setBonusFlash(null)}
      />

      <GuildBasePopup guild={openBaseGuild} onClose={() => setOpenBaseGuild(null)} />
    </div>
  );
}
